import { isDefine, isNumeric } from 'helpers';
import { isNumber, round } from 'lodash';
import moment from 'moment';
import 'moment-duration-format';
import { addSuffixToString, localeNumber } from './string';
import { parseStatsToCalculate } from './parseBodyForSendCampaign';
import { momentInstance, timeBetween2Dates } from './date-time';
import { timezoneHelpers } from 'providers/TimezoneProvider';

export const calculateInsight = (number, dividend) => {
  if (dividend) {
    return (number / dividend) * 100;
  }

  return 0;
};

export const calculateRemainingTime = ({
  total,
  processed,
  created,
  updated,
  format = 'H:mm:ss',
}) => {
  const differentBySecond = moment(updated || undefined).diff(created, 'second');

  let remainingTime = '';
  if (processed) {
    const timePerItems = processed / differentBySecond;
    const remainingItems = total - processed;
    const resultBySecond = remainingItems / timePerItems;

    remainingTime = moment().startOf('day').seconds(resultBySecond).format(format);
  }

  return remainingTime;
};

export const calculatedPage = ({ total = 5, itemPerPage = 5 }) => {
  return Math.ceil(Number(total) / Number(itemPerPage)) || 1;
};

export const calculatedSpeed = (speed) =>
  isNumber(speed) ? Math.round((speed * 180) / 100) - 45 : -45;

export const statsCalculator = {
  percentage({
    total = 0,
    sent = 0,
    delivered,
    clicked,
    replied,
    failed,
    optout,
    block,
    attemptFailed,
    convertor,
    callback,
    listenCounter,
    ivrAnswered,
    ivrOptout,
    ivrClickThrough,
  }) {
    let sentPercentage = sent,
      deliveredPercentage = 0,
      clickedPercentage = 0,
      repliedPercentage = 0,
      failedPercentage = 0,
      optoutPercentage = 0,
      blockPercentage = 0,
      attemptFailedPercentage = attemptFailed,
      convertorPercentage = 0,
      callbackPercentage = 0,
      listenCounterPercentage = 0,
      ivrAnsweredPercentage = 0,
      ivrOptoutPercentage = 0,
      ivrClickThroughPercentage = 0;

    if (sent > 0) {
      deliveredPercentage =
        sent && delivered ? addSuffixToString(((delivered / sent) * 100).toFixed(2), '%') : `0%`;
      clickedPercentage =
        delivered && clicked
          ? addSuffixToString(((clicked / delivered) * 100).toFixed(2), '%')
          : `0%`;
      repliedPercentage =
        delivered && replied
          ? addSuffixToString(((replied / delivered) * 100).toFixed(2), '%')
          : `0%`;
      failedPercentage =
        sent && failed ? addSuffixToString(((failed / sent) * 100).toFixed(2), '%') : `0%`;
      optoutPercentage =
        sent && optout ? addSuffixToString(((optout / sent) * 100).toFixed(2), '%') : `0%`;
      blockPercentage =
        sent && block ? addSuffixToString(((block / sent) * 100).toFixed(2), '%') : `0%`;
      convertorPercentage =
        delivered && convertor
          ? addSuffixToString(((convertor / delivered) * 100).toFixed(2), '%')
          : `0%`;
      callbackPercentage =
        delivered && callback
          ? addSuffixToString(((callback / delivered) * 100).toFixed(2), '%')
          : `0%`;
      listenCounterPercentage =
        delivered && listenCounter
          ? addSuffixToString(((listenCounter / delivered) * 100).toFixed(2), '%')
          : `0%`;
    } else {
      //* case: sent === 0
      deliveredPercentage = delivered ? localeNumber(delivered) : `0%`;
      clickedPercentage = clicked ? localeNumber(clicked) : `0%`;
      repliedPercentage = replied ? localeNumber(replied) : `0%`;
      failedPercentage = failed ? localeNumber(failed) : `0%`;
      optoutPercentage = optout ? localeNumber(optout) : `0%`;
      blockPercentage = block ? localeNumber(block) : `0%`;
      convertorPercentage = convertor ? localeNumber(convertor) : `0%`;
      callbackPercentage = callback ? localeNumber(callback) : `0%`;
      listenCounterPercentage = listenCounter ? localeNumber(listenCounter) : `0%`;
    }

    if (total) {
      sentPercentage = sent ? addSuffixToString(((sent / total) * 100).toFixed(2), '%') : `0%`;
      attemptFailedPercentage = attemptFailed
        ? addSuffixToString(((attemptFailed / total) * 100).toFixed(2), '%')
        : `0%`;
    }

    //* Calculate IVR Stats
    if (delivered > 0) {
      ivrAnsweredPercentage = isNumeric(ivrAnswered)
        ? addSuffixToString(((ivrAnswered / delivered) * 100).toFixed(2), '%')
        : `0%`;
      ivrOptoutPercentage = isNumeric(ivrOptout)
        ? addSuffixToString(((ivrOptout / delivered) * 100).toFixed(2), '%')
        : `0%`;
      ivrClickThroughPercentage = isNumeric(ivrClickThrough)
        ? addSuffixToString(((ivrClickThrough / delivered) * 100).toFixed(2), '%')
        : `0%`;
    } else {
      ivrAnsweredPercentage = ivrAnswered ? localeNumber(ivrAnswered) : `0%`;
      ivrOptoutPercentage = ivrOptout ? localeNumber(ivrOptout) : `0%`;
      ivrClickThroughPercentage = ivrClickThrough ? localeNumber(ivrClickThrough) : `0%`;
    }

    return {
      deliveredPercentage,
      clickedPercentage,
      repliedPercentage,
      failedPercentage,
      optoutPercentage,
      blockPercentage,
      sentPercentage,
      attemptFailedPercentage: total ? attemptFailedPercentage : attemptFailed || 0,
      convertorPercentage,
      callbackPercentage,
      listenCounterPercentage,
      ivrAnsweredPercentage,
      ivrOptoutPercentage,
      ivrClickThroughPercentage,
    };
  },
};

export const gaugeCalculator = {
  calculatorTicksGauge(maxValue = 220, part = 12) {
    let result = [];
    let listNumberOfElementByPart = [];
    for (let i = 0; i <= part; i++) {
      listNumberOfElementByPart = [...listNumberOfElementByPart, i];
    }

    result = listNumberOfElementByPart
      .map((el, idx) => {
        return Math.ceil((maxValue * el) / part);
      })
      .map((el) => el.toString());

    return result.map((el, index) => {
      if (index !== result.length - 1) {
        //* Convert from 1xxx, 2xxx, 3xxx -> 1000, 2000, 3000, ...
        const prefixNumber = [...Array(el.length - 1).keys()].map(() => 0).join('');
        return `${el[0]}${prefixNumber}`;
      }

      return el;
    });
  },
};

export const costCalculator = {
  costPerClicked: ({ cost = 0, clicked = 0, isReturnNumber = false }) => {
    if (clicked) {
      return isReturnNumber
        ? parseFloat((cost / clicked).toFixed(3))
        : `$${(cost / clicked).toFixed(3)}`;
    }

    return isReturnNumber ? 0 : `$0`;
  },

  costPerMessage: ({ cost = 0, sent = 0, isReturnNumber = false }) => {
    if (sent) {
      return isReturnNumber ? parseFloat((cost / sent).toFixed(3)) : `$${(cost / sent).toFixed(3)}`;
    }

    return isReturnNumber ? 0 : `$0`;
  },

  revenue: (revenue = 0, { isPercent } = {}) => {
    let prefix = '$';
    let suffix = '';

    if (isPercent) {
      prefix = '';
      suffix = '%';
    }

    return `${prefix}${isNumeric(revenue) ? localeNumber(round(revenue, 1)) : 0}${suffix}`;
  },

  profit: ({ revenue, cost, profit }) => {
    //* If profit is defined -> convert it
    if (isDefine(profit)) {
      return localeNumber(Math.round(profit));
    }

    //* If not -> will calculate it by revenue - cost
    return `$${
      isNumeric(revenue) && isNumeric(cost) ? localeNumber(Math.round(revenue - cost)) : 0
    }`;
  },

  total: ({ cost = 0 }) => {
    return `$${cost.toFixed(3)}`;
  },
};

export const getAllOffsetLoadMore = ({ offset, limit }, eachLoop = () => {}) => {
  const currentPage = Math.ceil(offset / limit) + 1;
  for (let page = 0; page < currentPage; page++) {
    const eachOffset = page * limit;
    eachLoop && eachLoop(eachOffset, limit);
  }
};

export const checkCostFloat = (value) => {
  if (!isNumeric(value)) return value;
  const nextCost = Number.isInteger(value)
    ? '$' +
      (value + '')
        .split('')
        .reverse()
        .map((el, index) => {
          if (index % 3 === 0 && index !== 0) {
            return el + ',';
          }
          return el;
        })
        .reverse()
        .join('')
    : '$' + value.toFixed(3);

  return nextCost;
};

export const genCalculateStatsByField = (stats = {}, field) => {
  const statsCollected = parseStatsToCalculate(stats);
  const {
    deliveredPercentage,
    clickedPercentage,
    repliedPercentage,
    failedPercentage,
    optoutPercentage,
    blockPercentage,
    callbackPercentage,
    listenCounterPercentage,
  } = statsCalculator.percentage(statsCollected);

  const callDurationSecond = isNaN(
    (stats?.callDurationInSecond || 0) / (statsCollected?.delivered || 0)
  )
    ? 0
    : (stats?.callDurationInSecond || 0) / (statsCollected?.delivered || 0);

  const mapping = {
    delivered: `${deliveredPercentage} (${statsCollected.delivered})`,
    clicked: `${clickedPercentage} (${statsCollected.clicked})`,
    replied: `${repliedPercentage} (${statsCollected.replied})`,
    failed: `${failedPercentage} (${statsCollected.failed})`,
    block: `${blockPercentage} (${statsCollected.block})`,
    optout: `${optoutPercentage} (${statsCollected.optout})`,
    callback: `${callbackPercentage} (${statsCollected.callback})`,
    listen: `${listenCounterPercentage} (${statsCollected.listenCounter})`,
    attemptFailed: `${statsCollected.attemptFailed}`,
    revenue: `$${stats?.[field] || 0}`,
    callDurationInSecond: `${round(callDurationSecond, 3)} sec`,
  };

  return mapping?.[field] || stats?.[field];
};

export const calculateIVR = {
  ivrClickRate: ({ outboundIvrClickThrough, inboundIvrClickThrough, delivered, callback }) => {
    return isDefine(outboundIvrClickThrough) && isDefine(inboundIvrClickThrough)
      ? parseFloat(
          (
            ((outboundIvrClickThrough + inboundIvrClickThrough) / (delivered + callback)) *
            100
          ).toFixed(2)
        ) || 0
      : null;
  },
  ivrOptoutRate: ({ outboundIvrOptout, inboundIvrOptout, delivered, callback }) => {
    return isDefine(outboundIvrOptout) && isDefine(inboundIvrOptout)
      ? parseFloat(
          (((outboundIvrOptout + inboundIvrOptout) / (delivered + callback)) * 100).toFixed(2)
        ) || 0
      : null;
  },
};

export const calculatePerformanceChecking = ({ lastSentClicked, firstSentClicked, lastSent }) => {
  const format = 'HH:mm:ss YYYY-MM-DD';

  return {
    lastSent: lastSent ? timezoneHelpers.showDateTimeByTimezone(lastSent, { format: format }) : '',
    timeBetween1:
      lastSent && lastSentClicked
        ? timeBetween2Dates(
            timezoneHelpers.showDateTimeByTimezone(lastSent, { returnMomentObject: true }),
            timezoneHelpers.showDateTimeByTimezone(lastSentClicked, { returnMomentObject: true }),
            {
              isABS: true,
            }
          ).remaining
        : '',
    lastSentClicked: lastSentClicked
      ? timezoneHelpers.showDateTimeByTimezone(lastSentClicked, { format: format })
      : '',
    timeBetween2:
      lastSentClicked && firstSentClicked
        ? timeBetween2Dates(
            timezoneHelpers.showDateTimeByTimezone(lastSentClicked, { returnMomentObject: true }),
            timezoneHelpers.showDateTimeByTimezone(firstSentClicked, { returnMomentObject: true }),
            {
              isABS: true,
            }
          ).remaining
        : '',
    firstSentClicked: firstSentClicked
      ? timezoneHelpers.showDateTimeByTimezone(firstSentClicked, { format: format })
      : '',
  };
};

export const calculateRevenueCampaign = ({
  revenue,
  cost,
  sent,
  outboundIvrClickDurationSec,
  inboundIvrClickDurationSec,
  delivered,
}) => {
  const million = 1000000;

  return {
    profit: revenue - cost,
    roi: !!cost ? ((revenue - cost) / cost) * 100 : (revenue - cost) * 100,
    revPerCall: !!delivered ? revenue / delivered : 0,
    revPerMil: sent ? (revenue * million) / sent : 0,
    revPerMilMessage: !!delivered ? (revenue * million) / delivered : 0,
    revPerMilMinute:
      (revenue * million) / ((outboundIvrClickDurationSec + inboundIvrClickDurationSec) / 60),
  };
};

// export const calculateRemainingTime = ({ total, processed, currentTime, startTime }) => {
//   return (total - processed) / (processed / (currentTime - startTime));
// };
